import FileSaver from 'file-saver';
import { consoleErrorMsg } from './common';
import { TColumnConfig } from './types';

declare global {
  interface Window {
    System: SystemJS;
  }
}

interface SystemJS {
  import: (module: string) => Promise<any>,
}

interface ExcelServiceSettings {
  mimeType: string;
  extension: string;
}

class ExcelService {
  private excelJSInstance;
  private settings = {
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: '.xlsx',
  };

  constructor(excelSettings?: Partial<ExcelServiceSettings>) {
    Object.assign(this.settings, excelSettings);
  }

  async getExcelJS() {
    try {
      if (!this.excelJSInstance) this.excelJSInstance = await window.System.import('exceljs');
      return { excelJS: this.excelJSInstance, error: null };
    } catch (error) {
      consoleErrorMsg(error, 'ExcelService -> getExcelJS');
      return { excelJS: null, error };
    }
  }

  // Order in values matters
  makeSingleItemRow(values: string[], config: TColumnConfig) {
    const { emptyColumns, totalColumns, filler } = config;
    const cells = [];
    for (let index = 0; index < totalColumns; index++) {
      if (emptyColumns.includes(index) || index > values.length) cells.push(filler);
      else cells.push(values[index] || ' ');
    }
    return cells;
  }

  // Mutates worksheet
  hideEmptyColumns(worksheet) {
    const columns = worksheet.columnCount;
    for (let index = 1; index <= columns; index++) {
      let notEmpty = false;
      const eColumn = worksheet.getColumn(index);
      if (eColumn.hidden) continue;
      eColumn.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
        if (rowNumber > 1 && String(cell.value).trim() !== '') {
          notEmpty = true;
        }
      });
      eColumn.hidden = !notEmpty;
    }
  }

  // Mutates row
  populateRow(rowValues: string[], row) {
    rowValues.forEach((value, index) => {
      // eslint-disable-next-line no-param-reassign
      row.getCell(index + 1).value = value;
    });
    return row;
  }

  async saveExcel(workbook, fileName) {
    try {
      const excelData = await workbook.xlsx.writeBuffer();
      const excelFile = new Blob([ excelData ], { type: this.settings.mimeType });
      FileSaver.saveAs(excelFile, fileName + this.settings.extension);
    } catch (error) {
      consoleErrorMsg(error, 'ExcelService -> saveExcel');
    }
  }
}

export const ExcelJSService = new ExcelService();
